import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Table,
  Checkbox,
  Select,
  message,
  Upload,
  Modal,
  DatePicker,
  Radio,
  Alert,
  Row,
  Col,
  Space,
  Tag,
} from "antd";
import {
  UploadOutlined,
  DownloadOutlined,
  UserAddOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import api from "../../../services/api";
import moment from "moment";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import Papa from "papaparse";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { all_routes } from "../../router/all_routes";
import CommonSelect from "../../../core/common/commonSelect";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";


interface Lead {
  id: string | number;
  name: string;
  email: string;
  mobileNumber: string;
  address: string;
  country: string;
  state: string;
  district: string;
  city: string;
  status?: string;
  createdAt?: string;
  userIds?: number[];
  other?: string;
}

interface User {
  key: number;
  companyId: number;
  companyType: string;
  createdAt: string;
  department: number;
  designation: number;
  dob: string;
  doj: string;
  email: string;
  fname: string;
  lname: string;
  gender: string;
  mobileNo: string;
  phoneCode: string;
  profilePic?: string;
  reportingOfficer: number;
  role: number;
  roleChar: string;
  salutation: string;
  status: number;
  uid: string;
  username: string;
}

interface Company {
  companyId: number;
  companyName: string;
  companyType: string;
}

interface Counts {
  assigned?: number;
  unassigned?: number;
  total?: number;
  [key: string]: number | undefined;
}

interface UploadHistoryRecord {
  file_name: string;
  original_file_name: string;
  upload_date: string;
  total_leads: number;
  assigned_leads: number;
  unassigned_leads: number;
  assignment_status: 'fully_assigned' | 'partially_assigned' | 'unassigned';
  assigned_percentage: number;
}

const CallLogsLeadsOperation: React.FC = () => {
  const user = useSelector((state: any) => state.user.user);

  const [leads, setLeads] = useState<Lead[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [counts, setCounts] = useState<Counts>({});
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState({
    companyId: "",
    companyName: "Select Company",
    companyType: "",
  });
  const [selectedLeads, setSelectedLeads] = useState<(string | number)[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>("unassigned");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [isAssignedUsersModalVisible, setIsAssignedUsersModalVisible] =
    useState(false);
  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [currentLeadUsers, setCurrentLeadUsers] = useState<User[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<"csv" | "excel" | "pdf">(
    "csv"
  );
  const [downloadStartDate, setDownloadStartDate] = useState<Dayjs | null>(
    null
  );
  const [downloadEndDate, setDownloadEndDate] = useState<Dayjs | null>(null);
  const [downloadStatus, setDownloadStatus] = useState<string>("");
  const STATUS_OPTIONS = [
    { label: "All Statuses", value: "" },
    { label: "Assigned", value: "assigned" },
    { label: "Unassigned", value: "unassigned" },
  ];
  const [uploadHistory, setUploadHistory] = useState<any[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [isUploadHistoryVisible, setIsUploadHistoryVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyStartDate, setHistoryStartDate] = useState<Dayjs | null>(null);
  const [historyEndDate, setHistoryEndDate] = useState<Dayjs | null>(null);
  const [localFilter, setLocalFilter] = useState<'all' | 'assigned' | 'unassigned'>('all');

  const getSignature = (): string | null => localStorage.getItem("signature");

  const withSignature = <T extends object>(
    data?: T
  ): T & { signature: string | null } => ({
    ...(data || ({} as T)),
    signature: getSignature(),
  });

  const disabledDate = (current: any) => {
    if (!current) return false;
    const threeMonthsAgo = moment().subtract(3, "months");
    return moment(current).isBefore(threeMonthsAgo, "day");
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    setStartDate(date);
    if (date && endDate && endDate.isBefore(date)) {
      setEndDate(null);
    }
  };
  const handleEndDateChange = (date: Dayjs | null) => {
    setEndDate(date);
  };

  const handleDownloadStartDateChange = (date: Dayjs | null) => {
    setDownloadStartDate(date);
    if (date && endDate && endDate.isBefore(date)) {
      setDownloadEndDate(null);
    }
  };

  const handleDownloadEndDateChange = (date: Dayjs | null) => {
    setDownloadEndDate(date);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setShowDatePicker(value === "");

    if (value !== "") {
      setStartDate(null);
      setEndDate(null);
    } else {
      setLeads([]);
      setCounts({});
    }
  };

  const handleManualDownload = async () => {
    if (!selectedCompany.companyId) {
      message.warning("Please select a company first");
      return;
    }

    if (downloadStartDate && downloadEndDate) {
      const threeMonthsAgo = dayjs(3, "months");
      if (downloadStartDate.isBefore(threeMonthsAgo)) {
        message.error("Start date cannot be older than 3 months from today");
        return;
      }

      const monthsDiff = downloadEndDate.diff(downloadStartDate, "months");
      if (monthsDiff > 3) {
        message.error("Date range cannot exceed 3 months");
        return;
      }

      if (downloadEndDate.isBefore(downloadStartDate)) {
        message.error("End date cannot be before start date");
        return;
      }
    }

    setDownloadLoading(true);
    try {
      const response = await api.post<{ data?: any }>(
        "callLogsService/getDownloadLeads",
        withSignature({
          status: downloadStatus,
          companyId: selectedCompany.companyId,
          companyType: selectedCompany.companyType,
          startDate: downloadStartDate
            ? downloadStartDate.format("YYYY-MM-DD")
            : null,
          endDate: downloadEndDate
            ? downloadEndDate.format("YYYY-MM-DD")
            : null,
        })
      );

      const leadsData = response?.data?.data?.data || [];

      const formattedLeads = leadsData.map((lead: any) => ({
        ...lead,
        userIds: lead.userIds
          ? typeof lead.userIds === "string"
            ? JSON.parse(lead.userIds)
            : lead.userIds
          : [],
      }));

      switch (selectedFormat) {
        case "csv":
          downloadAsCSV(formattedLeads);
          break;
        case "excel":
          downloadAsExcel(formattedLeads);
          break;
        case "pdf":
          downloadAsPDF(formattedLeads, downloadStartDate, downloadEndDate);
          break;
      }

      message.success(
        `Downloaded ${leadsData.length
        } leads as ${selectedFormat.toUpperCase()}`
      );
      setIsDownloadModalVisible(false);

      // Reset form
      setDownloadStartDate(null);
      setDownloadEndDate(null);
      setDownloadStatus("");
      setSelectedFormat("csv");
    } catch (error: any) {
      if (error.response?.status === 400) {
        message.error(
          error.response?.data?.message || "Date range exceeds 3 months limit"
        );
      } else {
        message.error("Failed to download leads");
        console.error("Error downloading leads:", error);
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  const downloadAsCSV = (leads: Lead[]) => {
    const csvData = leads.map((lead) => ({
      Name: lead.name,
      Email: lead.email,
      Phone: lead.mobileNumber,
      Country: lead.country,
      State: lead.state,
      District: lead.district,
      City: lead.city,
      Address: lead.address,
      Other: lead.other || "-",
      Status: lead.status || "Unassigned",
      "Assigned Users":
        lead.userIds && lead.userIds.length > 0
          ? lead.userIds
            .map((id) => {
              const user = users.find((user) => user.key === id);
              return user ? `${user.fname} ${user.lname}` : "-";
            })
            .join(", ")
          : "-",
      "Created Date": lead.createdAt
        ? moment(lead.createdAt).format("DD-MM-YYYY")
        : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(csvData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");

    const fileName = `leads_${moment().format("YYYY_MM_DD_HH_mm_ss")}.csv`;
    XLSX.writeFile(wb, fileName);
  };

  const downloadAsExcel = (leads: Lead[]) => {
    const excelData = leads.map((lead) => ({
      Name: lead.name,
      Email: lead.email,
      Phone: lead.mobileNumber,
      Country: lead.country,
      State: lead.state,
      District: lead.district,
      City: lead.city,
      Address: lead.address,
      Other: lead.other || "-",
      Status: lead.status || "Unassigned",
      "Assigned Users":
        lead.userIds && lead.userIds.length > 0
          ? lead.userIds
            .map((id) => {
              const user = users.find((user) => user.key === id);
              return user ? `${user.fname} ${user.lname}` : "-";
            })
            .join(", ")
          : "-",
      "Created Date": lead.createdAt
        ? moment(lead.createdAt).format("DD-MM-YYYY")
        : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");

    const fileName = `leads_${moment().format("YYYY_MM_DD_HH_mm_ss")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const downloadAsPDF = (
    leads: Lead[],
    startDate: Dayjs | null,
    endDate: Dayjs | null
  ) => {
    const doc = new jsPDF();
    const columns = [
      "Name",
      "Email",
      "Phone",
      "Address",
      "Other",
      "Status",
      "Assigned Users",
      "Created Date",
    ];

    const tableData = leads.map((lead) => [
      lead.name,
      lead.email,
      lead.mobileNumber,
      lead.address,
      lead.other || "-",
      lead.status || "Unassigned",
      lead.userIds && lead.userIds.length > 0
        ? lead.userIds
          .map((id) => {
            const user = users.find((user) => user.key === id);
            return user ? `${user.fname} ${user.lname}` : "-";
          })
          .join(", ")
        : "-",
      lead.createdAt ? moment(lead.createdAt).format("DD-MM-YYYY") : "-",
    ]);

    // Add title
    doc.setFontSize(16);
    doc.text("Leads Report", 14, 15);
    doc.setFontSize(10);
    doc.text(
      `Company: ${selectedCompany.companyName} (${selectedCompany.companyType})`,
      14,
      25
    );

    if (startDate && endDate) {
      doc.text(
        `Date Range: ${startDate.format("DD/MM/YYYY")} - ${endDate.format(
          "DD/MM/YYYY"
        )}`,
        14,
        32
      );
    } else {
      doc.text(`Date: All Dates`, 14, 32);
    }

    doc.text(`Status: ${downloadStatus || "All Statuses"}`, 14, 39);
    doc.text(`Total Records: ${leads.length}`, 14, 46);
    doc.text(`Generated: ${moment().format("DD/MM/YYYY HH:mm")}`, 14, 53);

    // Add table
    doc.autoTable({
      head: [columns],
      body: tableData,
      startY: 60,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    const fileName = `leads_${moment().format("YYYY_MM_DD_HH_mm_ss")}.pdf`;
    doc.save(fileName);
  };

  const fetchCompanyList = async () => {
    setIsLoading(true);
    try {
      const response = await api.post("userService/getAllCompanyList", {
        signature: getSignature(),
      });
      setCompanyList(response.data.data.data);

      const newCompany = {
        companyId: response.data.data.data[0].id,
        companyName: response.data.data.data[0].name,
        companyType: response.data.data.data[0].type,
      };
      setSelectedCompany(newCompany);
      fetchAllUsers(newCompany.companyId, newCompany.companyType);
    } catch (error) {
      console.error("Error fetching companies:", error);
      message.error("Failed to fetch companies");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllUsers = async (
    companyId: number,
    companyType: string
  ): Promise<void> => {
    try {
      const response = await api.post<any>(
        "userService/getAllEmployeesListDataByCompanyId",
        withSignature({
          companyId,
          companyType,
        })
      );
      const userData = response.data?.data?.data.allUsers || [];
      const mappedUsers = userData.map((user: any) => ({
        key: user.id,
        companyId: user.company_id,
        companyType: user.account_type,
        createdAt: user.createdAt,
        department: user.department,
        designation: user.designation,
        dob: user.dob,
        doj: user.doj,
        email: user.email,
        fname: user.fname,
        lname: user.lname,
        gender: user.gender,
        mobileNo: user.mobile_no,
        phoneCode: user.phone_code,
        profilePic: user.profile_pic,
        reportingOfficer: user.reporting_officer,
        role: user.role,
        roleChar: user.role_char,
        salutation: user.salutation,
        status: user.status,
        uid: user.uid,
        username: user.username,
      }));
      setUsers(mappedUsers);
      setSelectedUserIds([]);
    } catch (error) {
      message.error("Failed to fetch users");
      console.error("Error fetching users:", error);
    }
  };

  const fetchUploadHistory = async (startDate?: Dayjs | null, endDate?: Dayjs | null) => {
    if (!selectedCompany.companyId) {
      message.warning("Please select a company first");
      return;
    }

    setHistoryLoading(true);
    try {
      const payload: any = {
        companyId: selectedCompany.companyId,
        companyType: selectedCompany.companyType,
      };

      // Add date range if provided
      if (startDate && endDate) {
        payload.startDate = startDate.format("YYYY-MM-DD");
        payload.endDate = endDate.format("YYYY-MM-DD");
      }

      const response = await api.post(
        "callLogsService/getUploadedFileNames",
        withSignature(payload)
      );

      const files = response.data?.data || [];

      // Format for display
      const formattedHistory = files.map((file: any) => ({
        file_name: file.file_name,
        original_file_name: file.original_file_name,
        upload_date: moment(file.last_uploaded).format("DD-MM-YYYY HH:mm:ss"),
        first_uploaded: moment(file.first_uploaded).format("DD-MM-YYYY HH:mm:ss"),
        total_leads: file.total_leads,
        assigned_leads: file.assigned_leads,
        unassigned_leads: file.unassigned_leads,
        has_assigned_leads: file.has_assigned_leads,
        assignment_status: file.assignment_status,
        assigned_percentage: file.assigned_percentage,
      }));

      setUploadHistory(formattedHistory);

      // Show success message with count
      message.success(`Found ${files.length} uploads in selected date range`);
    } catch (error) {
      console.error("Error fetching upload history:", error);
      message.error("Failed to fetch upload history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const deleteBatch = async (fileName: string) => {
    if (!selectedCompany.companyId) {
      message.warning("Please select a company first");
      return;
    }

    Modal.confirm({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete all leads from "${fileName}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await api.post(
            "callLogsService/deleteBatchByFileName",
            withSignature({
              companyId: selectedCompany.companyId,
              companyType: selectedCompany.companyType,
              file_name: fileName,
            })
          );

          const deletedCount = response.data?.data?.deletedCount || 0;
          message.success(`Successfully deleted ${deletedCount} leads from batch`);

          fetchUploadHistory();

          if (selectedFile === fileName) {
            setSelectedFile(null);
            setLeads([]);
          }
        } catch (error) {
          console.error("Error deleting batch:", error);
          message.error("Failed to delete batch");
        }
      }
    });
  };

  // Function to get leads by file name
  const getLeadsByFileName = async (fileName: string) => {
    if (!selectedCompany.companyId) {
      message.warning("Please select a company first");
      return;
    }

    setLoading(true);
    setSelectedFile(fileName);
    setLocalFilter('all');
    try {
      const response = await api.post(
        "callLogsService/getLeadsByFileName",
        withSignature({
          companyId: selectedCompany.companyId,
          companyType: selectedCompany.companyType,
          file_name: fileName,
        })
      );

      const parsedLeads = response.data?.data || [];
      setLeads(parsedLeads);
      setCounts({ total: parsedLeads.length });
      message.success(`Loaded ${parsedLeads.length} leads from ${fileName}`);
    } catch (error) {
      console.error("Error fetching leads by file:", error);
      message.error("Failed to fetch leads from file");
    } finally {
      setLoading(false);
    }
  };

  const downloadBatch = async (fileName: string) => {
    try {
      const response = await api.post(
        "callLogsService/getLeadsByFileName",
        withSignature({
          companyId: selectedCompany.companyId,
          companyType: selectedCompany.companyType,
          file_name: fileName,
        })
      );

      const leadsData = response.data?.data || [];

      if (leadsData.length === 0) {
        message.warning("No leads found in this batch");
        return;
      }

      // Format data for CSV
      const csvData = leadsData.map((lead: any) => ({
        Name: lead.name,
        Email: lead.email,
        Phone: lead.mobileNumber,
        Country: lead.country,
        State: lead.state,
        District: lead.district,
        City: lead.city,
        Address: lead.address,
        Other: lead.other || "-",
        Status: lead.status || "Unassigned",
        "Created Date": lead.createdAt
          ? moment(lead.createdAt).format("DD-MM-YYYY HH:mm:ss")
          : "-",
      }));

      const ws = XLSX.utils.json_to_sheet(csvData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Leads");

      const safeFileName = fileName.replace(/[^\w\s.-]/gi, '');
      const downloadName = `batch_${safeFileName}_${moment().format('YYYY_MM_DD')}.xlsx`;
      XLSX.writeFile(wb, downloadName);

      message.success(`Downloaded ${leadsData.length} leads from ${fileName}`);
    } catch (error) {
      console.error("Error downloading batch:", error);
      message.error("Failed to download batch");
    }
  };

  useEffect(() => {
    fetchCompanyList();
  }, []);

  const handleCompanyChange = async (selectedOption: any) => {
    if (!selectedOption) return;
    setIsLoading(true);
    const newCompany = {
      companyId: selectedOption.value,
      companyName: selectedOption.label.split("-")[0].trim(),
      companyType: selectedOption.label.split("-")[1].trim(),
    };
    setSelectedCompany(newCompany);
    fetchAllUsers(newCompany.companyId, newCompany.companyType);
    const response = await api.post(
      "callLogsService/getUploadedFileNames",
      withSignature({
        companyId: newCompany.companyId,
        companyType: newCompany.companyType,
      })
    );
    const fileNames = response.data?.data || [];
    setUploadHistory(fileNames);
  };

  const handleBeforeUpload = (file: File) => {
    if (!selectedCompany) {
      message.warning("Please select a company before uploading");
      return false;
    }
    setFileToUpload(file);
    Modal.confirm({
      title: "Confirm Upload",
      content: `Are you sure you want to upload this file for ${selectedCompany.companyName}?`,
      okText: "Yes",
      cancelText: "No",
      onOk: () => handleUpload(file),
      onCancel: () => {
        setFileToUpload(null);
        return false;
      },
    });
    return false;
  };

  const handleDownloadCsv = () => {
    if (!selectedCompany) {
      message.warning("Please select a company before uploading");
      return false;
    }
    const ws = XLSX.utils.json_to_sheet([], {
      header: [
        "name",
        "email",
        "mobileNumber",
        "country",
        "state",
        "district",
        "city",
        "address",
        "address_two",
        "other",
      ],
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");
    XLSX.writeFile(wb, "leads_data.csv");
    return false;
  };

  const generateUniqueFileName = (file: File, companyId: string) => {
    const timestamp = Date.now();
    const sanitizedName = file.name.replace(/\s+/g, '_');
    return `${timestamp}_${sanitizedName}`;
  };

  const handleUpload = (file: File) => {
    if (!selectedCompany) {
      message.error("No company selected");
      return;
    }
    const uniqueFileName = generateUniqueFileName(
      file,
      selectedCompany.companyId
    );
    setLoading(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        const leadsData = result.data.map((row: any) => ({
          name: row.name,
          email: row.email,
          mobileNumber: row.mobileNumber,
          country: row?.country == "" ? null : row?.country,
          state: row?.state == "" ? null : row?.state,
          district: row?.district == "" ? null : row?.district,
          city: row?.city == "" ? null : row?.city,
          address: row?.address == "" ? null : row?.address,
          address_two: row?.address_two == "" ? null : row?.address_two,
          other: row?.other == "" ? null : row?.other,
          companyId: selectedCompany.companyId,
          companyType: selectedCompany.companyType,
          file_name: uniqueFileName,
        }));

        try {
          await api.post(
            "callLogsService/creak-bulk-call-logs-leads",
            withSignature({
              leads_bulk: leadsData,
            })
          );
          message.success("Leads uploaded successfully");
          if (uniqueFileName) {
            await getLeadsByFileName(uniqueFileName);
          }
        } catch (error) {
          message.error("Failed to upload leads");
          console.error("Error uploading leads:", error);
        } finally {
          setLoading(false);
          setFileToUpload(null);
        }
      },
      error: (error) => {
        message.error("Failed to parse CSV file");
        console.error("Error parsing CSV:", error);
        setLoading(false);
        setFileToUpload(null);
      },
    });
  };

  const handleSelectLead = (id: any, checked: any) => {
    if (checked) {
      setSelectedLeads((prev) => [...prev, id]);
    } else {
      setSelectedLeads((prev) => prev.filter((leadId) => leadId !== id));
    }
  };

  const handleSelectAll = (checked: boolean): void => {
    const unassignedLeads = leads.filter(
      (lead) => !lead.status || lead.status.toLowerCase() === "unassigned"
    );

    if (checked) {
      setSelectedLeads(unassignedLeads.map((lead) => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const assignLeadsToUser = async (): Promise<void> => {
    if (
      selectedUserIds.length === 0 ||
      selectedLeads.length === 0 ||
      !selectedCompany
    ) {
      message.warning("Please select a company, leads, and at least one user");
      return;
    }

    try {
      setLoading(true);
      await api.post(
        "callLogsService/assign-call-logs-leads",
        withSignature({
          leadIds: selectedLeads,
          userIds: selectedUserIds,
          companyType: selectedCompany.companyType,
          companyId: selectedCompany.companyId,
        })
      );
      message.success(`${selectedLeads.length} leads assigned successfully`);
      setSelectedLeads([]);
      setSelectedUserIds([]);
      if (selectedFile) {
        await getLeadsByFileName(selectedFile);
      }
    } catch (error) {
      message.error("Failed to assign leads");
      console.error("Error assigning leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const showAssignedUsers = (userIds: number[]) => {
    const assignedUsers = users.filter((user) => userIds.includes(user.key));
    setCurrentLeadUsers(assignedUsers);
    setIsAssignedUsersModalVisible(true);
  };

  const handleClearFilter = () => {
    setSelectedFile(null);
    setLocalFilter('all');
  };
  const filteredLeads = React.useMemo(() => {
    if (!selectedFile) {
      return leads;
    }

    if (localFilter === 'all') {
      return leads;
    } else if (localFilter === 'assigned') {
      return leads.filter(lead =>
        (lead.status === 'assigned') ||
        (lead.userIds && lead.userIds.length > 0)
      );
    } else if (localFilter === 'unassigned') {
      return leads.filter(lead =>
        (!lead.status || lead.status === 'unassigned') &&
        (!lead.userIds || lead.userIds.length === 0)
      );
    }
    return leads;
  }, [leads, selectedFile, localFilter]);

  const filteredCounts = React.useMemo(() => {
    if (!selectedFile) {
      return counts;
    }

    const total = filteredLeads.length;
    const assigned = filteredLeads.filter(lead =>
      (lead.status === 'assigned') ||
      (lead.userIds && lead.userIds.length > 0)
    ).length;
    const unassigned = total - assigned;

    return {
      total,
      assigned,
      unassigned
    };
  }, [filteredLeads, selectedFile, counts]);

  const columns = [
    {
      title: (
        <Checkbox
          indeterminate={
            selectedLeads.length > 0 &&
            selectedLeads.length <
            leads.filter(
              (lead) =>
                !lead.status || lead.status.toLowerCase() === "unassigned"
            ).length
          }
          checked={
            selectedLeads.length ===
            leads.filter(
              (lead) => !lead.status || lead.status === "unassigned"
            ).length && leads.length > 0
          }
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      render: (_: any, record: Lead) => {
        if (!record.status || record.status === "unassigned") {
          return (
            <Checkbox
              checked={selectedLeads.includes(record.id)}
              onChange={(e) => handleSelectLead(record.id, e.target.checked)}
            />
          );
        }
        return null;
      },
      width: 50,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <Link
            to="#"
            onClick={(e: any) => e.preventDefault()}
            className="avatar avatar-md border rounded-circle"
          >
            <ImageWithBasePath
              src={record.Image || "assets/img/users/user.png"}
              className="img-fluid"
              alt="img"
            />
          </Link>
          <div className="ms-2">
            <h6 className="fw-medium">{text}</h6>
          </div>
        </div>
      ),
      sorter: (a: Lead, b: Lead) => a.name.localeCompare(b.name),
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a: Lead, b: Lead) => a.email.localeCompare(b.email),
    },
    {
      title: "Phone",
      dataIndex: "mobileNumber",
      sorter: (a: Lead, b: Lead) =>
        a.mobileNumber.localeCompare(b.mobileNumber),
    },
    {
      title: "Country",
      dataIndex: "country",
      sorter: (a: Lead, b: Lead) => a.country.localeCompare(b.country),
    },
    {
      title: "State",
      dataIndex: "state",
      sorter: (a: Lead, b: Lead) => a.state.localeCompare(b.state),
    },
    {
      title: "District",
      dataIndex: "district",
      sorter: (a: Lead, b: Lead) => a.district.localeCompare(b.district),
    },
    {
      title: "City",
      dataIndex: "city",
      sorter: (a: Lead, b: Lead) => a.city.localeCompare(b.city),
    },
    {
      title: "Address",
      dataIndex: "address",
      sorter: (a: Lead, b: Lead) => a.address.localeCompare(b.address),
    },
    {
      title: "Other",
      dataIndex: "other",
      sorter: (a: Lead, b: Lead) => (a.other || "").localeCompare(b.other || ""),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => status || "Unassigned",
      sorter: (a: Lead, b: Lead) =>
        (a.status || "Unassigned").localeCompare(b.status || "Unassigned"),
    },
    {
      title: "Assigned Users",
      dataIndex: "userIds",
      render: (userIds: number[]) => {
        if (!userIds || userIds.length === 0) return "-";

        return (
          <>
            <span className="me-2">
              {userIds.length} user{userIds.length > 1 ? "s" : ""}
            </span>
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                showAssignedUsers(userIds);
              }}
            />
          </>
        );
      },
      sorter: (a: Lead, b: Lead) => {
        const aCount = a.userIds?.length || 0;
        const bCount = b.userIds?.length || 0;
        return aCount - bCount;
      },
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      render: (value: string) =>
        value ? moment(value).format("DD-MM-YYYY") : "-",
      sorter: (a: Lead, b: Lead) =>
        moment(a.createdAt).unix() - moment(b.createdAt).unix(),
    },
  ];

  const uploadHistoryColumns = [
    {
      title: 'File Name',
      dataIndex: 'original_file_name',
      render: (text: string, record: UploadHistoryRecord) => (
        <div>
          <div
            className="fw-medium"
            style={{
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {text}
          </div>
          <small className="text-muted" style={{ fontSize: '10px' }}>
            {record.file_name}
          </small>
        </div>
      ),
      width: 200,
    },
    {
      title: 'Upload Date',
      dataIndex: 'upload_date',
      width: 150,
    },
    {
      title: 'Leads Info',
      width: 120,
      render: (_: any, record: UploadHistoryRecord) => (
        <div>
          <div><strong>Total:</strong> {record.total_leads}</div>
          <div style={{ color: '#52c41a' }}>
            <strong>✓ Assigned:</strong> {record.assigned_leads}
          </div>
          <div style={{ color: '#fa8c16' }}>
            <strong>○ Unassigned:</strong> {record.unassigned_leads}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      width: 120,
      render: (_: any, record: UploadHistoryRecord) => {
        const getStatusColor = () => {
          switch (record.assignment_status) {
            case 'fully_assigned': return 'success';
            case 'partially_assigned': return 'warning';
            default: return 'default';
          }
        };

        const getStatusText = () => {
          switch (record.assignment_status) {
            case 'fully_assigned': return 'FULLY ASSIGNED';
            case 'partially_assigned': return 'PARTIALLY ASSIGNED';
            default: return 'UNASSIGNED';
          }
        };

        return (
          <Tag color={getStatusColor()}>
            {getStatusText()}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      width: 200,
      render: (_: any, record: UploadHistoryRecord) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              getLeadsByFileName(record.file_name);
              setIsUploadHistoryVisible(false);
            }}
            title="View leads from this upload"
          >
            View
          </Button>
          {user?.userDetails?.role_char?.toLowerCase() !== "employee" && (
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => downloadBatch(record.file_name)}
              title="Download this batch"
            >
              Download
            </Button>
          )}
          <Button
            type="link"
            danger
            icon={<i className="ti ti-trash" />}
            onClick={() => deleteBatch(record.file_name)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div
      className="page-wrapper"
      style={{
        paddingTop: "64px",
        minHeight: "100vh",
        backgroundColor: "#f0f2f5",
      }}
    >
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Calls Data Upload</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Application</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Bulk Upload
                  {selectedFile && (
                    <span className="ms-2 text-primary" style={{ position: 'relative' }}>
                      | Viewing: {selectedFile.substring(selectedFile.indexOf('_') + 1)}
                      <Button
                        type="link"
                        size="small"
                        onClick={handleClearFilter}
                        className="ms-1"
                        style={{
                          fontSize: '10px',
                          padding: '0 4px',
                          opacity: 0.7,
                          color: '#666'
                        }}
                        title="Clear file filter"
                      >
                        <i className="ti ti-x" />
                      </Button>
                    </span>
                  )}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="me-3">
              <Button
                icon={<DownloadOutlined />}
                loading={loading}
                onClick={handleDownloadCsv}
              >
                Download Template
              </Button>
            </div>

            <div className="me-3">
              <Upload
                accept=".csv"
                beforeUpload={handleBeforeUpload}
                showUploadList={false}
                disabled={loading || !selectedCompany}
                className="me-3"
              >
                <Button icon={<UploadOutlined />} loading={loading}>
                  Upload CSV
                </Button>
              </Upload>
            </div>

            <div className="me-3">
              <CommonSelect
                options={companyList.map((company: any) => ({
                  value: company.id,
                  label: `${company.name} - ${company.type}`,
                }))}
                defaultValue={{
                  value: selectedCompany.companyId,
                  label: `${selectedCompany.companyName} - ${selectedCompany.companyType}`,
                }}
                onChange={handleCompanyChange}
              />
            </div>
            <div className="me-3">
              <Button
                icon={<i className="ti ti-history" />}
                onClick={() => {
                  setIsUploadHistoryVisible(true);
                }}
                loading={historyLoading}
              >
                Upload History
              </Button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row">
          <div className="col-lg-3 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-primary flex-shrink-0">
                    <i className="ti ti-users fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      {selectedFile ? 'Filtered Data' : 'Total Data'}
                    </p>
                    <h4>{filteredCounts.total !== undefined ? filteredCounts.total : filteredLeads.length}</h4>
                    {selectedFile && (
                      <small className="text-muted">From {leads.length} total in file</small>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-primary flex-shrink-0">
                    <i className="ti ti-user-check fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      {selectedFile ? 'Filtered Assigned' : 'Assigned Data'}
                    </p>
                    <h4>
                      {filteredCounts.assigned !== undefined
                        ? filteredCounts.assigned
                        : filteredLeads.filter(
                          (lead) => lead.userIds && lead.userIds.length > 0
                        ).length}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-primary flex-shrink-0">
                    <i className="ti ti-user-x fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      {selectedFile ? 'Filtered Unassigned' : 'Unassigned Data'}
                    </p>
                    <h4>
                      {filteredCounts.unassigned !== undefined
                        ? filteredCounts.unassigned
                        : filteredLeads.filter(
                          (lead) => !lead.userIds || lead.userIds.length === 0
                        ).length}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-primary flex-shrink-0">
                    <i className="ti ti-user-plus fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      Selected Data
                    </p>
                    <h4>{selectedLeads.length}</h4>
                    {selectedFile && localFilter !== 'all' && (
                      <small className="text-muted">Filter: {localFilter}</small>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Data Card */}
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h2></h2>

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">

              <Space
                direction="vertical"
                size="middle"
                style={{ display: "flex" }}
              >
                <Select
                  placeholder="Filter leads"
                  className="mb-2"
                  style={{ width: "150px", height: "58px", padding: "10px" }}
                  onChange={(value) => {
                    if (selectedFile) {
                      setLocalFilter(value as 'all' | 'assigned' | 'unassigned');
                    } else {
                      handleStatusFilterChange(value);
                    }
                  }}
                  value={selectedFile ? localFilter : statusFilter}
                  options={[
                    { value: "all", label: "All Leads" },
                    { value: "unassigned", label: "Unassigned" },
                    { value: "assigned", label: "Assigned" },
                  ]}
                />
              </Space>
              <Select
                placeholder="Select users to assign"
                style={{ width: 250, maxHeight: 80, overflowY: "auto" }}
                onChange={(value: number) => {
                  setSelectedUserIds(value ? [value] : []);
                }}
                options={users.map((user) => {
                  const fullName = `${user.fname ?? ""} ${user.lname ?? ""}`.trim();
                  return {
                    value: user.key,
                    search: fullName.toLowerCase(),
                    label: fullName,
                    user: user
                  };
                })}
                loading={loading}
                value={selectedUserIds.length > 0 ? selectedUserIds[0] : undefined}
                optionFilterProp="search"
                showSearch
                filterOption={(input, option) =>
                  (option?.search as string)?.includes(
                    input.trim().toLowerCase()
                  )
                }
                className="me-2 mb-2"
                dropdownStyle={{ maxHeight: 200, overflowY: "auto" }}
                allowClear
                optionRender={(option) => {
                  const user = users.find((u) => u.key === option.value);
                  const fullName = `${user?.fname ?? ""} ${user?.lname ?? ""}`.trim();

                  return (
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      {user?.profilePic ? (
                        <img
                          src={user.profilePic}
                          alt={user?.fname}
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            backgroundColor: "#ccc",
                            color: "#000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: "bold",
                          }}
                        >
                          {(user?.fname?.[0] ?? "?").toUpperCase()}
                        </div>
                      )}
                      <span>{fullName}</span>
                    </div>
                  );
                }}
                labelRender={(props) => {
                  const user = users.find((u) => u.key === props.value);
                  const fullName = `${user?.fname ?? ""} ${user?.lname ?? ""}`.trim();

                  if (!user) return props.label;

                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6
                      }}
                    >
                      {user?.profilePic ? (
                        <img
                          src={user.profilePic}
                          alt={user?.fname}
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            backgroundColor: "#ccc",
                            color: "#000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 10,
                            fontWeight: "bold",
                          }}
                        >
                          {(user?.fname?.[0] ?? "?").toUpperCase()}
                        </div>
                      )}
                      <span>{fullName}</span>
                    </div>
                  );
                }}
              />

              <Button
                type="primary"
                className="mb-2"
                style={{ minWidth: "100px", height: "37px" }}
                icon={<UserAddOutlined />}
                onClick={assignLeadsToUser}
                disabled={
                  selectedUserIds.length === 0 ||
                  selectedLeads.length === 0 ||
                  !selectedCompany
                }
                loading={loading}
              >
                Assign ({selectedLeads.length})
              </Button>
            </div>
          </div>
          <div className="card-body p-0">
            {users.length > 0 && (
              <Table<Lead>
                className="table datanew dataTable no-footer"
                columns={columns}
                rowHoverable={false}
                dataSource={filteredLeads}
                pagination={{
                  locale: { items_per_page: "" },
                  nextIcon: <i className="ti ti-chevron-right" />,
                  prevIcon: <i className="ti ti-chevron-left" />,
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "30"],
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Assigned Users Modal */}
      <Modal
        title="Assigned Users"
        visible={isAssignedUsersModalVisible}
        onCancel={() => setIsAssignedUsersModalVisible(false)}
        footer={[
          <Button
            key="close"
            onClick={() => setIsAssignedUsersModalVisible(false)}
          >
            Close
          </Button>,
        ]}
      >
        <div className="table-responsive">
          <table className="table table-striped custom-table mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {currentLeadUsers.length > 0 ? (
                currentLeadUsers.map((user) => (
                  <tr key={user.key}>
                    <td>
                      <div className="d-flex align-items-center">
                        {user.profilePic ? (
                          <ImageWithBasePath
                            src={user.profilePic}
                            className="rounded-circle me-2"
                            width={30}
                            height={30}
                            alt="User"
                          />
                        ) : (
                          <div
                            className="avatar-initial rounded-circle bg-primary text-white me-2"
                            style={{
                              width: 30,
                              height: 30,
                              lineHeight: "30px",
                              textAlign: "center",
                            }}
                          >
                            {user.fname.charAt(0)}
                            {user.lname.charAt(0)}
                          </div>
                        )}
                        <span>
                          {user.fname} {user.lname}
                        </span>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      {user.phoneCode} {user.mobileNo}
                    </td>
                    <td>{user.roleChar}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center">
                    No assigned users
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Modal>

      {/* Download Leads Modal - Manual Selection */}
      <Modal
        title="Download Leads"
        visible={isDownloadModalVisible}
        onCancel={() => {
          setIsDownloadModalVisible(false);
          setDownloadStartDate(null);
          setDownloadEndDate(null);
          setDownloadStatus("");
          setSelectedFormat("csv");
        }}
        footer={null}
        width={600}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <div className="form-group">
              <label>
                <strong>Start Date</strong>
              </label>
              <DatePicker
                style={{ width: "100%" }}
                value={downloadStartDate}
                onChange={handleDownloadStartDateChange}
                disabledDate={disabledDate}
                format="DD/MM/YYYY"
                placeholder="Select start date"
              />
              <small className="text-muted">
                Cannot be older than 3 months
              </small>
            </div>
          </Col>

          <Col span={12}>
            <div className="form-group">
              <label>
                <strong>End Date</strong>
              </label>
              <DatePicker
                style={{ width: "100%" }}
                value={downloadEndDate}
                onChange={handleDownloadEndDateChange}
                disabledDate={(current) => {
                  if (!current) return false;
                  const threeMonthsAgo = dayjs().subtract(3, "months");
                  if (dayjs(current).isBefore(threeMonthsAgo, "day")) {
                    return true;
                  }
                  if (
                    downloadStartDate &&
                    dayjs(current).isBefore(downloadStartDate)
                  ) {
                    return true;
                  }
                  return false;
                }}
                format="DD/MM/YYYY"
                placeholder="Select end date"
              />
              <small className="text-muted">Max 3 months from start date</small>
            </div>
          </Col>
        </Row>

        <div className="form-group" style={{ marginTop: 16 }}>
          <label>
            <strong>Status Filter</strong>
          </label>
          <Select
            style={{ width: "100%" }}
            placeholder="Select status"
            allowClear
            value={downloadStatus || undefined}
            onChange={(value) => setDownloadStatus(value ?? "")}
            options={STATUS_OPTIONS}
          />

          <small className="text-muted">
            Leave empty to download all statuses
          </small>
        </div>

        <div className="form-group" style={{ marginTop: 16 }}>
          <label>
            <strong>Download Format</strong>
          </label>
          <Radio.Group
            value={selectedFormat}
            onChange={(e) => setSelectedFormat(e.target.value)}
            buttonStyle="solid"
            style={{ width: "100%" }}
          >
            <Radio.Button
              value="csv"
              style={{ width: "33.33%", textAlign: "center" }}
            >
              <FileTextOutlined /> CSV
            </Radio.Button>
            <Radio.Button
              value="excel"
              style={{ width: "33.33%", textAlign: "center" }}
            >
              <FileExcelOutlined /> Excel
            </Radio.Button>
            <Radio.Button
              value="pdf"
              style={{ width: "33.33%", textAlign: "center" }}
            >
              <FilePdfOutlined /> PDF
            </Radio.Button>
          </Radio.Group>
        </div>

        <div className="form-group" style={{ marginTop: 16 }}>
          <label>
            <strong>Company Information</strong>
          </label>
          <div
            style={{
              padding: "10px",
              background: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            <p>
              <strong>Company:</strong> {selectedCompany.companyName}
            </p>
            <p>
              <strong>Type:</strong> {selectedCompany.companyType}
            </p>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 20,
          }}
        >
          <Button
            onClick={() => {
              setIsDownloadModalVisible(false);
              setDownloadStartDate(null);
              setDownloadEndDate(null);
              setDownloadStatus("");
              setSelectedFormat("csv");
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleManualDownload}
            loading={downloadLoading}
            icon={
              selectedFormat === "csv" ? (
                <FileTextOutlined />
              ) : selectedFormat === "excel" ? (
                <FileExcelOutlined />
              ) : (
                <FilePdfOutlined />
              )
            }
          >
            Download {selectedFormat.toUpperCase()}
          </Button>
        </div>
      </Modal>

      {/* Upload History Modal */}
      <Modal
        title="Upload History"
        visible={isUploadHistoryVisible}
        onCancel={() => {
          setIsUploadHistoryVisible(false);
          setHistoryStartDate(null);
          setHistoryEndDate(null);
        }}
        footer={null}
        width={1000}
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={9}>
            <DatePicker
              placeholder="Start Date"
              style={{ width: '100%' }}
              value={historyStartDate}
              onChange={(date) => {
                setHistoryStartDate(date);
                if (date && historyEndDate) {
                  const threeMonthsLater = date.add(3, 'month');
                  if (dayjs(historyEndDate).isAfter(threeMonthsLater, 'day')) {
                    setHistoryEndDate(null);
                    message.warning('End date reset as it exceeded 3 months from new start date');
                  }
                }
              }}
              disabledDate={(current) => {
                if (!current) return false;
                return current > dayjs().endOf('day');
              }}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col span={9}>
            <DatePicker
              placeholder="End Date"
              style={{ width: '100%' }}
              value={historyEndDate}
              onChange={(date) => {
                setHistoryEndDate(date);
              }}
              disabledDate={(current) => {
                if (!current) return false;

                const today = dayjs();
                const currentDate = dayjs(current);
                if (currentDate.isAfter(today, 'day')) return true;

                if (historyStartDate) {
                  const startDate = dayjs(historyStartDate);

                  if (currentDate.isBefore(startDate, 'day')) {
                    return true;
                  }

                  const threeMonthsLater = startDate.add(3, 'month');
                  return currentDate.isAfter(threeMonthsLater, 'day');
                }

                return false;
              }}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col span={3}>
            <Button
              type="primary"
              onClick={() => {
                if (!historyStartDate || !historyEndDate) {
                  message.error("Please select both start and end dates");
                  return;
                }

                const start = dayjs(historyStartDate).startOf('day');
                const end = dayjs(historyEndDate).startOf('day');

                if (end.isBefore(start, 'day')) {
                  message.error("End date cannot be before start date");
                  return;
                }

                const monthsDiff = end.diff(start, 'month', true);
                if (monthsDiff > 3) {
                  message.error("Date range cannot exceed 3 months");
                  return;
                }

                fetchUploadHistory(historyStartDate, historyEndDate);
              }}
              loading={historyLoading}
              style={{ width: '100%' }}
            >
              Filter
            </Button>
          </Col>
          <Col span={3}>
            <Button
              onClick={() => {
                setHistoryStartDate(null);
                setHistoryEndDate(null);
                // setUploadHistory([]);
              }}
              style={{ width: '100%' }}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>

        {uploadHistory.length > 0 ? (
          <div className="table-responsive">
            <Table
              dataSource={uploadHistory}
              loading={historyLoading}
              columns={uploadHistoryColumns}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
              }}
            />
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <i className="ti ti-file-search" style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
            <h4 style={{ color: '#999', marginBottom: '8px' }}>
              {historyLoading ? 'Loading...' : 'No upload history to display'}
            </h4>
            <p style={{ color: '#999' }}>
              {historyStartDate || historyEndDate
                ? 'Select a date range and click "Filter" to view uploads'
                : 'Select a date range and click "Filter" to view upload history'}
            </p>
          </div>
        )}

      </Modal>
    </div>
  );
};

export default CallLogsLeadsOperation;
